import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import useHubConfigSync, {
  clearSyncResult,
  refreshUnsyncedConfigs,
  syncAndReboot,
} from '../../hooks/useHubConfigSync';
import { Toast } from '../ui';
import styles from './HubConfigSyncBar.module.scss';

// Warning strip under the machine workspace tabs, shown while the selected hub
// has configs saved in the cloud that it has not pulled yet.
function HubConfigSyncBar({ serialNumber }) {
  const { pathname } = useLocation();
  const { unsyncedConfigs, syncing, result } = useHubConfigSync(serialNumber);

  // Re-check on every workspace tab switch so the bar never lags far behind
  useEffect(() => {
    if (serialNumber) {
      refreshUnsyncedConfigs(serialNumber);
    }
  }, [serialNumber, pathname]);

  const count = unsyncedConfigs?.length ?? 0;

  return (
    <>
      {(count > 0 || syncing) && (
        <div className={styles.bar} role="status">
          <AlertTriangle size={16} className={styles.icon} />
          <div className={styles.text}>
            <strong>
              {syncing
                ? 'Syncing configuration to the hub…'
                : `${count} configuration${count === 1 ? '' : 's'} not yet synced to the hub`}
            </strong>
            {count > 0 && <span>{unsyncedConfigs.join(', ')}</span>}
          </div>
          <button
            type="button"
            className={styles.syncButton}
            onClick={() => syncAndReboot(serialNumber)}
            disabled={syncing}
          >
            <RefreshCw size={14} className={syncing ? styles.spinning : ''} />
            {syncing ? 'Syncing…' : 'Sync & Reboot'}
          </button>
        </div>
      )}
      {result && (
        <Toast tone={result.tone} onClose={() => clearSyncResult(serialNumber)}>
          {result.message}
        </Toast>
      )}
    </>
  );
}

export default HubConfigSyncBar;
